/**
 * FlashAlert Component
 *
 * A toast notification component that slides in from the bottom
 * and auto-dismisses after a configurable duration.
 *
 * Features:
 * - Slide-in/out animations
 * - Auto-dismiss with configurable duration
 * - Click to dismiss
 * - Semantic variants (success, error, info)
 * - Optional icon display
 * - Keyboard accessible
 *
 * Ported from Vue FlashAlert.vue
 *
 * @example
 * ```tsx
 * <FlashAlert
 *   alert={{
 *     message: 'Changes saved successfully',
 *     variant: 'success',
 *     icon: 'check',
 *     duration: 3000,
 *   }}
 *   onDismiss={() => console.log('dismissed')}
 * />
 * ```
 */

import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@/components/ui/display/Icon";
import { cx } from "@/utils/cx";
import type { FlashAlertProps } from "./types";
import styles from "./FlashAlert.module.css";

export const FlashAlert: React.FC<FlashAlertProps> = ({
  alert,
  onDismiss,
  className = "",
  "data-testid": testId,
}) => {
  const [isHiding, setIsHiding] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  const { message, icon, duration = 3000, variant = "info" } = alert;

  // Handle dismiss
  const handleDismiss = useCallback(() => {
    setIsHiding(true);
    // Wait for animation to complete before calling onDismiss
    setTimeout(() => {
      setIsVisible(false);
      onDismiss?.();
    }, 500); // Match slide-out animation duration
  }, [onDismiss]);

  // Auto-dismiss after duration
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, handleDismiss]);

  // Handle keyboard dismiss
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape" || event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleDismiss();
    }
  };

  // Build class names - only base class + custom className
  const containerClasses = cx(styles.container, className);

  if (!isVisible) {
    return null;
  }

  const alertContent = (
    <div
      className={containerClasses}
      data-variant={variant}
      data-hiding={isHiding || undefined}
      onClick={handleDismiss}
      onKeyDown={handleKeyDown}
      role="alert"
      aria-live="polite"
      tabIndex={0}
      data-testid={testId}
    >
      <span data-component-name="message">{message}</span>
      {icon && <Icon name={icon} size="md" aria-hidden={true} />}
    </div>
  );

  // Render in portal to ensure it's above all other content
  return createPortal(alertContent, document.body);
};

FlashAlert.displayName = "FlashAlert";
